// normalizeFlightQuery — pure sanitization of the airline/flight-number query.
//
// Extracted verbatim from `FlightLookup.js` (the inline sanitization at the top
// of `handleLookupFlight`) as part of the behavior-preserving refactor
// (issue #240). The airline is trimmed, uppercased, and stripped to `[A-Z0-9]`;
// the flight number is trimmed and stripped to digits; the FlightAware `ident`
// is their concatenation `${airline}${flightNumber}`.
//
// Totality preserved exactly: each input is coerced with `(value || '')` before
// trimming, so `null`/`undefined`/missing inputs yield `''` (and an `ident` of
// `''`), matching the source.
//
// React-free and side-effect-free.
//
// Validates: Requirements 3.1, 3.4, 3.5, 1.6

/**
 * @typedef {Object} NormalizedFlightQuery
 * @property {string} airline - Trimmed, uppercased, `[A-Z0-9]`-only airline code.
 * @property {string} flightNumber - Trimmed, digits-only flight number.
 * @property {string} flightIdent - The FlightAware ident (`airline` + `flightNumber`).
 */

/**
 * Normalizes the raw airline and flight-number inputs into a FlightAware query.
 *
 * @param {string|null|undefined} airlineInput - Raw airline code from the form.
 * @param {string|null|undefined} flightNumberInput - Raw flight number from the form.
 * @returns {NormalizedFlightQuery} The sanitized `{ airline, flightNumber, flightIdent }`.
 */
export function normalizeFlightQuery(airlineInput, flightNumberInput) {
  // Sanitize and trim airline and flight number inputs to remove any special characters
  const airline = (airlineInput || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
  const flightNumber = (flightNumberInput || '').trim().replace(/[^0-9]/g, '');
  const flightIdent = `${airline}${flightNumber}`;

  return { airline, flightNumber, flightIdent };
}

export default normalizeFlightQuery;
